<template>
  <div v-if="modelValue" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6 border-b dark:border-gray-700">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
          {{ isLayoutMode ? tt('New layout', 'Nova postavitev') : tt('New page', 'Nova stran') }}
        </h2>
      </div>

      <div class="p-6 space-y-4">
        <!-- Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {{ isLayoutMode ? tt('Layout name', 'Ime postavitve') : tt('Page name', 'Ime strani') }}
          </label>
          <input
            v-model="name"
            type="text"
            :placeholder="isLayoutMode ? tt('e.g. Ground floor', 'npr. Pritličje') : tt('e.g. Home', 'npr. Dom')"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            @keyup.enter="create"
            autofocus />
        </div>

        <!-- Icon Picker -->
        <IconPicker
          v-model="icon"
          :label="tt('Icon', 'Ikona')"
          fallback="dashboard"
          :placeholder="tt('Search icons (e.g. dashboard, analytics)', 'Išči ikone (npr. dashboard, analytics)')" />

        <!-- What a LAYOUT is drawn on. A page has no type: how it arranges its
             cards is a setting, not a kind of page (owner: *"multiple dashboard
             pages now.. we said we remove dashboards, columns"*). -->
        <div v-if="isLayoutMode">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {{ tt('Drawn on', 'Narisano na') }}
          </label>
          <select
            v-model="layoutType"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            <option value="canvas">{{ tt('Canvas', 'Platno') }}</option>
            <option v-if="canCreateBimPage" value="bim">{{ tt('3D model', '3D model') }}</option>
          </select>
          <p v-if="!canCreateBimPage && dashboardStore.hasHubBimDashboard" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {{ tt('This hub already has its 3D layout — a hub holds one 3D model and one layout for it.', 'To vozlišče že ima svojo 3D postavitev — vozlišče ima en 3D model in eno postavitev zanj.') }}
          </p>
        </div>

        <!-- Picture library: a canvas can be put on a plan this hub already
             holds, instead of uploading the same drawing again. A canvas with no
             picture is simply a diagram. -->
        <div v-if="layoutType === 'canvas'">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {{ tt('Floor plan under it (optional)', 'Tloris pod njim (neobvezno)') }}
          </label>
          <select
            v-model="basePlanId"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            <option value="">{{ tt('Upload one later', 'Nalo?im jo pozneje') }}</option>
            <option v-for="plan in plans" :key="plan.id" :value="plan.id">
              {{ plan.name }}{{ plan.usedBy ? ` ? ${plan.usedBy}` : '' }}
            </option>
          </select>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {{
              tt(
                'Plans belong to the hub, so two pages can share one drawing.',
                'Tlorisi pripadajo vozli??u, zato si dve strani lahko delita eno risbo.',
              )
            }}
          </p>
        </div>

        <!-- Grid Settings are gone with the `grid` TYPE: a page arranges its own
             cards. Kept out of the UI on purpose. -->
      </div>

      <div class="p-6 border-t dark:border-gray-700 flex justify-end gap-2">
        <button
          @click="$emit('update:modelValue', false)"
          class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
          Cancel
        </button>
        <button
          @click="create"
          :disabled="!name.trim()"
          class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {{ isLayoutMode ? tt('Create layout', 'Ustvari postavitev') : tt('Create page', 'Ustvari stran') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useDashboardStore } from '@/stores/dashboard';
import { apiFetch } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import IconPicker from '@/components/inputs/IconPicker.vue';
import { tt } from '@/utils/consoleText';
import { isAdminRole } from '@/utils/roles';
import type { DashboardLayoutConfigLike } from '@/components/dashboard/dashboardTypes';

interface Props {
  modelValue: boolean;
  initialLayoutType?: 'column' | 'board' | 'overview' | 'canvas' | 'bim' | 'grid';
  /**
   * What this dialog creates: a PAGE (the surface an owner lands on — no type,
   * every screen reads the same page) or a LAYOUT (a canvas, which needs a base).
   */
  mode?: 'page' | 'layout';
}

const props = defineProps<Props>();
const emit = defineEmits(['update:modelValue', 'created']);

const dashboardStore = useDashboardStore();
const authStore = useAuthStore();
const name = ref('');
const icon = ref('dashboard');

/**
 * The ONE page type. `column`, `grid` and `overview` are retired as kinds — how a
 * page arranges its cards is the page's own business — so a new page is always the
 * board, which is also the shape a phone can swipe.
 */
const DEFAULT_PAGE_TYPE = 'board' as const;

const layoutType = ref<'column' | 'board' | 'overview' | 'canvas' | 'bim' | 'grid'>(
  props.initialLayoutType ?? DEFAULT_PAGE_TYPE,
);

const isLayoutMode = computed(() =>
  props.mode
    ? props.mode === 'layout'
    : props.initialLayoutType === 'canvas' || props.initialLayoutType === 'bim',
);

/**
 * The 3D page is the HUB's one shared surface (`t_hub_bim_model`): only an admin
 * creates it, and only while the hub has none. One model per hub means one page
 * per hub; the hub's own POST refuses a second one regardless.
 */
const canCreateBimPage = computed(
  () => isAdminRole(authStore.user?.role) && !dashboardStore.hasHubBimDashboard,
);
const columnCount = ref(3);

/** The hub's plan library, and the plan this page is created on (empty = later). */
interface PlanOption {
  id: string;
  name: string;
  usedBy?: number;
}
const plans = ref<PlanOption[]>([]);
const basePlanId = ref('');

async function loadPlans(): Promise<void> {
  try {
    const rows = await apiFetch('/layouts/plans');
    plans.value = (Array.isArray(rows) ? rows : []).map((row: Record<string, unknown>) => ({
      id: String(row.id ?? ''),
      name: String(row.name ?? ''),
      usedBy: typeof row.usedBy === 'number' ? row.usedBy : 0,
    }));
  } catch {
    plans.value = [];
  }
}
const SHARED_LAYOUT_DEFAULTS = {
  displayMode: 'layout-only',
  belowColumns: 2,
  aspectRatioPresetId: 'widescreen-16-9',
};
const BIM_LAYOUT_DEFAULTS = {
  displayMode: 'layout-only',
  belowColumns: 2,
};
/** A page's own shape: columns side by side, the first one the house. */
const PAGE_DEFAULTS = { columns: 3 };

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return;
    layoutType.value = props.initialLayoutType ?? (isLayoutMode.value ? 'canvas' : DEFAULT_PAGE_TYPE);
    if (layoutType.value === 'bim' && !canCreateBimPage.value) layoutType.value = 'canvas';
    void loadPlans();
  },
  { immediate: true },
);

async function create() {
  if (!name.value.trim()) return;
  if (layoutType.value === 'bim' && !canCreateBimPage.value) return;

  try {
    const layoutConfig: DashboardLayoutConfigLike = {};
    if (!isLayoutMode.value) {
      // A page has no type to pick: it is the page, and its own settings decide
      // how the cards sit.
      Object.assign(layoutConfig, PAGE_DEFAULTS);
    } else if (layoutType.value === 'canvas') {
      Object.assign(layoutConfig, SHARED_LAYOUT_DEFAULTS);
      // A plan or a diagram IS a layout (`t_layouts`): give it the v2 marker so it
      // is drawn as one, and so `Edit` can put it on an automation page as a hero.
      // Without this a "Floor Plan" page created here was still a dashboard-era
      // page — no base, no hero, invisible outside its own route.
      layoutConfig.layout = {
        role: 'layout',
        version: 2,
        base: { kind: 'canvas' },
      };
    } else if (layoutType.value === 'bim') {
      Object.assign(layoutConfig, BIM_LAYOUT_DEFAULTS);
    }

    // A plan and a process drawing are ONE type now — `layout` — and the marker's
    // base says what sits underneath. The 3D page keeps its own type: it is the
    // hub's shared page, not a drawing that hangs off a category.
    const pageType = !isLayoutMode.value ? DEFAULT_PAGE_TYPE : layoutType.value === 'bim' ? 'bim' : 'layout';
    const dashboard = await dashboardStore.createDashboard(name.value, pageType, icon.value || undefined, {
      layoutConfig,
      basePlanId: layoutType.value === 'canvas' && basePlanId.value ? basePlanId.value : null,
    });

    emit('created', dashboard);
    emit('update:modelValue', false);

    // Reset
    name.value = '';
    icon.value = 'dashboard';
    layoutType.value = props.initialLayoutType ?? (isLayoutMode.value ? 'canvas' : DEFAULT_PAGE_TYPE);
    columnCount.value = 3;
    basePlanId.value = '';
  } catch (err) {
    console.error('Error creating dashboard:', err);
  }
}
</script>
